import React, { useState, useEffect, useRef } from "react";
import {
    Input,
    InputGroup,
    InputRightElement,
    Button,
    IconButton,
    Text,
    FormControl,
    FormErrorMessage,
    CircularProgress,
    Center,
    Box,
    Image,
    Container,
    VStack,
    FormLabel,
    Link,
    HStack,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAuth } from "./providers/AuthProvider";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { supabase } from "./utils/supabaseClient";

const forgotPasswordPath = (email) => {
    const trimmed = email?.trim() ?? "";
    return trimmed
        ? `/forgot-password?email=${encodeURIComponent(trimmed)}`
        : "/forgot-password";
};

const LoginPage = () => {
    const { login, auth } = useAuth();
    const [loginForm, setLoginForm] = useState({
        email: "",
        password: "",
    });
    const [errors, setErrors] = useState({});
    const [loginError, setLoginError] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [orgBranding, setOrgBranding] = useState({ logoUrl: null, name: "" });
    const navigate = useNavigate();
    const emailInputRef = useRef(null);
    const isSubmittingRef = useRef(false);

    const validate = () => {
        const reg = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;
        setLoginError("");
        const nextErrors = {};
        if (loginForm.email === undefined || loginForm.email === "") {
            nextErrors.email = "Email is required";
        } else if (!reg.test(loginForm.email)) {
            nextErrors.email = "Must be a valid email address";
        }
        if (loginForm.password === undefined || loginForm.password === "") {
            nextErrors.password = "Password is required";
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const loginUser = async (email, password) => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setLoading(true);
        try {
            const { error } = await login(email, password);
            if (error) {
                setLoginError(error.message);
                setLoading(false);
                isSubmittingRef.current = false;
                return;
            }
            // Keep spinner until auth flips and we navigate away
        } catch (error) {
            setLoginError(error.message);
            setLoading(false);
            isSubmittingRef.current = false;
        }
    };

    useEffect(() => {
        if (auth) {
            navigate("/enrolled-courses", { replace: true });
        }
    }, [auth, navigate]);

    useEffect(() => {
        let cancelled = false;

        const loadOrgBranding = async () => {
            try {
                const { data, error } = await supabase
                    .from("organization")
                    .select("logo_url, name")
                    .eq("status", "active")
                    .limit(1)
                    .maybeSingle();

                if (error) throw error;
                if (!cancelled && data) {
                    setOrgBranding({
                        logoUrl: data.logo_url || null,
                        name: data.name || "",
                    });
                }
            } catch (error) {
                console.error("Error loading organization branding:", error);
            }
        };

        loadOrgBranding();
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-focus email input on mount for better mobile UX
    useEffect(() => {
        const timer = setTimeout(() => {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
            }
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const handleLogin = () => {
        if (isLoading || isSubmittingRef.current) return;
        if (validate()) {
            setLoginError("");
            loginUser(loginForm.email, loginForm.password);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleLogin();
        }
    };

    return (
        <Box 
            minH="100vh" 
            w="100%" 
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            px={{ base: 4, md: 6 }}
            py={{ base: 8, md: 10 }}
            onKeyDown={handleKeyDown}
            bg="gray.50"
            backgroundImage="linear-gradient(180deg, #F7FAFC 0%, #EDF2F7 100%)"
            position="relative"
        >
            <Container maxW="container.sm" w="100%" px={0} flexShrink={0}>
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                >
                    <Box 
                        bg="white" 
                        px={{ base: 6, md: 10 }}
                        pt={{ base: 8, md: 10 }}
                        pb={{ base: 7, md: 9 }}
                        borderRadius="2xl" 
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.100"
                        textAlign="left"
                        w="100%"
                        maxW="420px"
                        mx="auto"
                    >
                        <VStack spacing={0} align="stretch">
                            <Box textAlign="center" mb={{ base: 7, md: 8 }}>
                                {orgBranding.logoUrl ? (
                                    <Image
                                        src={orgBranding.logoUrl}
                                        alt={orgBranding.name || "Organization logo"}
                                        w="100%"
                                        maxW={{ base: "260px", md: "300px" }}
                                        maxH={{ base: "88px", md: "108px" }}
                                        h="auto"
                                        objectFit="contain"
                                        mx="auto"
                                    />
                                ) : orgBranding.name ? (
                                    <Text
                                        fontSize={{ base: "lg", md: "xl" }}
                                        fontWeight="700"
                                        color="gray.800"
                                    >
                                        {orgBranding.name}
                                    </Text>
                                ) : null}
                            </Box>

                            <FormControl
                                isRequired
                                isInvalid={"email" in errors || "password" in errors}
                                w="100%"
                            >
                                <VStack spacing={4} align="stretch">
                                    <Box>
                                        <FormLabel 
                                            fontSize="sm"
                                            mb={1.5}
                                            color="gray.700"
                                            fontWeight="600"
                                        >
                                            Email
                                        </FormLabel>
                                        <Input
                                            ref={emailInputRef}
                                            id="email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={loginForm.email}
                                            isDisabled={isLoading}
                                            onChange={(e) => {
                                                setLoginForm({ ...loginForm, email: e.target.value });
                                                if (loginError) setLoginError("");
                                                if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                                            }}
                                            bg="white"
                                            borderColor="gray.300"
                                            size="lg"
                                            fontSize="16px"
                                            minH="48px"
                                            borderRadius="lg"
                                            autoComplete="email"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            inputMode="email"
                                            _placeholder={{ color: "gray.400" }}
                                            _hover={{ borderColor: "gray.400" }}
                                            _focus={{
                                                borderColor: "gray.700",
                                                boxShadow: "0 0 0 1px var(--chakra-colors-gray-700)",
                                            }}
                                        />
                                        {"email" in errors && (
                                            <FormErrorMessage fontSize="xs" mt={1}>
                                                {errors.email}
                                            </FormErrorMessage>
                                        )}
                                    </Box>

                                    <Box>
                                        <HStack justify="space-between" w="100%" mb={1.5} align="center">
                                            <FormLabel 
                                                fontSize="sm"
                                                mb={0}
                                                color="gray.700"
                                                fontWeight="600"
                                            >
                                                Password
                                            </FormLabel>
                                            <Link
                                                as={RouterLink}
                                                to={forgotPasswordPath(loginForm.email)}
                                                fontSize="sm"
                                                color="blue.600"
                                                fontWeight="500"
                                                _hover={{ color: "blue.800", textDecoration: "underline" }}
                                            >
                                                Forgot password?
                                            </Link>
                                        </HStack>
                                        <InputGroup size="lg">
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="••••••••"
                                                value={loginForm.password}
                                                isDisabled={isLoading}
                                                onChange={(e) => {
                                                    setLoginForm({ ...loginForm, password: e.target.value });
                                                    if (loginError) setLoginError("");
                                                    if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                                                }}
                                                bg="white"
                                                borderColor="gray.300"
                                                pr="12"
                                                fontSize="16px"
                                                minH="48px"
                                                borderRadius="lg"
                                                autoComplete="current-password"
                                                onKeyDown={handleKeyDown}
                                                _placeholder={{ color: "gray.400" }}
                                                _hover={{ borderColor: "gray.400" }}
                                                _focus={{
                                                    borderColor: "gray.700",
                                                    boxShadow: "0 0 0 1px var(--chakra-colors-gray-700)",
                                                }}
                                            />
                                            <InputRightElement height="48px" width="12">
                                                <IconButton
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    isDisabled={isLoading}
                                                    color="gray.500"
                                                    _hover={{ bg: "gray.100", color: "gray.700" }}
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                        {"password" in errors && (
                                            <FormErrorMessage fontSize="xs" mt={1}>
                                                {errors.password}
                                            </FormErrorMessage>
                                        )}
                                    </Box>

                                    {loginError && (
                                        <Text 
                                            color="red.500" 
                                            fontSize="sm"
                                            textAlign="left"
                                        >
                                            {loginError}
                                        </Text>
                                    )}

                                    {isLoading ? (
                                        <Center py={3}>
                                            <CircularProgress 
                                                isIndeterminate 
                                                size="32px"
                                                color="gray.700"
                                            />
                                        </Center>
                                    ) : (
                                        <Button
                                            onClick={handleLogin}
                                            mt={2}
                                            width="full"
                                            height="48px"
                                            color="white"
                                            bg="gray.800"
                                            borderRadius="lg"
                                            _hover={{ bg: "gray.700" }}
                                            _active={{ bg: "gray.900" }}
                                            isLoading={isLoading}
                                            fontSize="md"
                                            fontWeight="600"
                                        >
                                            Login
                                        </Button>
                                    )}
                                </VStack>
                            </FormControl>
                        </VStack>
                    </Box>
                </motion.div>
            </Container>

            <HStack mt={6} spacing={1.5} fontSize="xs" color="gray.400" flexShrink={0}>
                <Text>Powered by</Text>
                <Box
                    as="img"
                    src={`${process.env.PUBLIC_URL || ""}/Edthos.png`}
                    alt="Edthos"
                    h="16px"
                    w="auto"
                    objectFit="contain"
                    opacity={0.45}
                />
            </HStack>
        </Box>
    );
};

export default LoginPage;
